import { useCallback, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useToast } from '@chakra-ui/react';
import { WS_EVENTS } from '@twinden/shared';
import { api } from '../lib/api';
import { blockedIdsAtom, blocksAtom, currentUserAtom } from '../store';
import { useWebSocket } from './useWebSocket';
import { t } from '../i18n';

// Blocked people (M58, docs/report-block-contact.md D208).

/** People I blocked, and blocking and unblocking. */
export function useBlocks() {
  const [blocks, setBlocks] = useAtom(blocksAtom);
  const blockedIds = useAtomValue(blockedIdsAtom);

  const refresh = useCallback(async () => {
    const response = await api.getBlocks();
    setBlocks(response.data.blocks);
  }, [setBlocks]);

  const block = useCallback(
    async (userId: string) => {
      await api.blockUser(userId);
      await refresh();
    },
    [refresh]
  );

  const unblock = useCallback(
    async (userId: string) => {
      await api.unblockUser(userId);
      // At once, so the chat comes back without waiting for the list.
      setBlocks((current) =>
        (current ?? []).filter((entry) => entry.user.id !== userId)
      );
      await refresh();
    },
    [refresh, setBlocks]
  );

  return { blocks, blockedIds, refresh, block, unblock };
}

/**
 * Runs `reload` when my blocks change on another device or tab (D208): the
 * chat list fetches again, so an unblocked chat comes back there too, with
 * its latest message and unread count.
 */
export function useOnBlocksChanged(reload: () => unknown) {
  const { onEvent } = useWebSocket();
  useEffect(
    () =>
      onEvent(({ event }) => {
        if (event === WS_EVENTS.BLOCKS_CHANGED) void reload();
      }),
    [onEvent, reload]
  );
}

/**
 * Mounted once in the app shell: loads my blocks after sign-in and after a
 * reconnect, follows changes from my other devices, and tells a family's
 * roots when a member was reported (D207).
 */
export function useSafetySync() {
  const userId = useAtomValue(currentUserAtom)?.id;
  const { refresh } = useBlocks();
  const { onEvent, connectionEpoch } = useWebSocket();
  const toast = useToast();

  useEffect(() => {
    if (!userId) return;
    refresh().catch(() => undefined);
  }, [userId, refresh, connectionEpoch]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event === WS_EVENTS.BLOCKS_CHANGED) {
          refresh().catch(() => undefined);
          return;
        }
        if (event === WS_EVENTS.REPORT_CREATED) {
          const { familyName } = payload as { familyName?: string };
          toast({
            title: t('chat.reported.title', { family: familyName ?? '' }),
            description: t('chat.reported.hint'),
            status: 'info',
            duration: 8000,
            isClosable: true,
          });
        }
      }),
    [onEvent, refresh, toast]
  );
}
